import { Controller } from "@hotwired/stimulus"

// MSDS 배치 업로드 — 탭2 좌측.
// 다중 PDF 선택 → 선택 list 표시 → [모두 업로드·등록] → POST 후 우측 폴링 시작.
//
// 상호작용:
//   · openPicker        — [PDF 파일 선택] → OS picker 호출
//   · filesSelected     — picker 결과 list 에 누적 (같은 파일명 중복 제거)
//   · removeFile        — list 의 ✕ → 해당 파일 제거
//   · submit            — FormData 로 multipart POST → batch_id 받음 → 우측 폴링 트리거
export default class extends Controller {
  static targets = ["fileInput", "list", "count", "submitBtn", "deptInput", "dropZone", "listWrapper", "listCount"]
  static values = { createUrl: String }

  connect() {
    this.files = []
    this.refresh()
    // 부서 hidden field 변경 감지 — 부서 모달이 값 채울 때 활성 갱신
    if (this.hasDeptInputTarget) {
      this.deptInputTarget.addEventListener("change", () => this.refresh())
      // input 이벤트도 (직접 setValue 시)
      const observer = new MutationObserver(() => this.refresh())
      observer.observe(this.deptInputTarget, { attributes: true, attributeFilter: ["value"] })
    }

    // 전역 dragover/drop 방어 — 사용자가 실수로 dropZone 밖에 놓아도 브라우저가 파일을 새 탭으로 열지 않게.
    this._blockGlobalDrag = (e) => {
      if (!this.hasDropZoneTarget || !this.dropZoneTarget.contains(e.target)) {
        e.preventDefault()
        if (e.dataTransfer) e.dataTransfer.dropEffect = "none"
      }
    }
    window.addEventListener("dragover", this._blockGlobalDrag)
    window.addEventListener("drop", this._blockGlobalDrag)
  }

  disconnect() {
    if (this._blockGlobalDrag) {
      window.removeEventListener("dragover", this._blockGlobalDrag)
      window.removeEventListener("drop", this._blockGlobalDrag)
    }
  }

  openPicker() {
    this.fileInputTarget.click()
  }

  filesSelected(event) {
    const picked = Array.from(event.target.files || [])
    // 같은 (name, size) 조합 중복 제거
    picked.forEach(f => {
      const dup = this.files.some(existing => existing.name === f.name && existing.size === f.size)
      if (!dup) this.files.push(f)
    })
    // input 재초기화 — 같은 파일을 다시 선택해도 change 이벤트가 발생하도록
    this.fileInputTarget.value = ""
    this.refresh()
  }

  removeFile(event) {
    const idx = parseInt(event.currentTarget.dataset.index, 10)
    if (isNaN(idx)) return
    this.files.splice(idx, 1)
    this.refresh()
  }

  // dropZone dragover — 브라우저 기본 (파일 열기) 차단 + 시각 강조.
  dragOver(event) {
    event.preventDefault()
    event.dataTransfer.dropEffect = "copy"
    if (this.hasDropZoneTarget) {
      this.dropZoneTarget.classList.add("border-[var(--primary-700)]", "bg-[var(--primary-50)]")
      this.dropZoneTarget.classList.remove("border-[var(--gray-300)]")
    }
  }

  dragLeave(event) {
    // 자식 element 로 이동 시에도 dragleave 발생 → dropZone 밖으로 완전히 나갔을 때만 원복.
    if (event.currentTarget.contains(event.relatedTarget)) return
    this._resetDropZone()
  }

  drop(event) {
    event.preventDefault()
    this._resetDropZone()
    const dropped = Array.from(event.dataTransfer.files || [])
    // PDF 만 accept (MIME 또는 확장자)
    const pdfs = dropped.filter(f =>
      f.type === "application/pdf" || /\.pdf$/i.test(f.name)
    )
    const rejected = dropped.length - pdfs.length
    pdfs.forEach(f => {
      const dup = this.files.some(existing => existing.name === f.name && existing.size === f.size)
      if (!dup) this.files.push(f)
    })
    if (rejected > 0) {
      alert(`PDF 가 아닌 파일 ${rejected}개는 제외됐습니다.`)
    }
    this.refresh()
  }

  _resetDropZone() {
    if (!this.hasDropZoneTarget) return
    this.dropZoneTarget.classList.remove("border-[var(--primary-700)]", "bg-[var(--primary-50)]")
    this.dropZoneTarget.classList.add("border-[var(--gray-300)]")
  }

  refresh() {
    // 리스트 렌더
    this.listTarget.innerHTML = ""
    this.files.forEach((f, idx) => {
      const li = document.createElement("li")
      li.className = "flex items-center justify-between gap-2 px-2 py-1 bg-[var(--gray-50)] border border-[var(--gray-200)] rounded text-sm"
      const left = document.createElement("span")
      left.className = "flex items-center gap-2 truncate text-[var(--gray-800)]"
      left.innerHTML = `<i data-lucide="file-text" class="text-[var(--gray-500)]" style="width: 14px; height: 14px;"></i><span class="truncate">${this.escape(f.name)}</span><span class="text-xs text-[var(--gray-400)] flex-shrink-0">${this.fileSizeLabel(f.size)}</span>`
      const removeBtn = document.createElement("button")
      removeBtn.type = "button"
      removeBtn.dataset.action = "click->msds-batch-upload#removeFile"
      removeBtn.dataset.index = idx
      removeBtn.className = "flex-shrink-0 inline-flex items-center justify-center w-6 h-6 rounded text-[var(--gray-400)] hover:text-[var(--danger-700)] hover:bg-[var(--danger-50)] transition-colors"
      removeBtn.setAttribute("aria-label", "제거")
      removeBtn.innerHTML = `<i data-lucide="x" style="width: 14px; height: 14px;"></i>`
      li.appendChild(left)
      li.appendChild(removeBtn)
      this.listTarget.appendChild(li)
    })

    // count 갱신 — dropZone 안 문구 + 리스트 헤더의 count 라벨.
    this.countTarget.textContent = this.files.length === 0
      ? "선택된 파일 없음"
      : `선택된 파일: ${this.files.length}개`

    // 리스트 wrapper 노출/숨김 + 헤더 카운트
    if (this.hasListWrapperTarget) {
      this.listWrapperTarget.hidden = this.files.length === 0
    }
    if (this.hasListCountTarget) {
      this.listCountTarget.textContent = this.files.length > 0 ? `${this.files.length}개` : ""
    }

    // submit 활성/비활성 — 파일만 조건. 부서는 submit 클릭 시점 검증 (alert).
    this.submitBtnTarget.disabled = this.files.length === 0

    // lucide 아이콘 다시 그리기
    if (typeof window.lucide !== "undefined") {
      window.lucide.createIcons()
    }
  }

  async submit() {
    if (this.files.length === 0) return

    // 부서 미선택 시 alert.
    const dept = this.hasDeptInputTarget ? this.deptInputTarget.value : ""
    if (!dept) {
      alert("부서를 선택해주세요.")
      return
    }

    this.submitBtnTarget.disabled = true

    const formData = new FormData()
    if (this.hasDeptInputTarget) {
      formData.append("department_code", this.deptInputTarget.value || "")
    }
    const memoEl = document.getElementById("msds-batch-memo")
    if (memoEl) formData.append("memo", memoEl.value || "")
    this.files.forEach(f => formData.append("files[]", f))

    const csrf = document.querySelector('meta[name="csrf-token"]')?.content
    try {
      const res = await fetch(this.createUrlValue, {
        method: "POST",
        headers: { "X-CSRF-Token": csrf, "Accept": "application/json" },
        body: formData,
        credentials: "same-origin"
      })
      const json = await res.json()
      if (!json.success) {
        alert(`업로드 실패: ${json.error?.message || "알 수 없는 오류"}`)
        this.submitBtnTarget.disabled = false
        return
      }

      // 정상: 좌측 file list 초기화, 우측 폴링 트리거. 진행 상태는 우측 패인이 표시.
      this.files = []
      this.refresh()
      document.dispatchEvent(new CustomEvent("msds-batch:created", { detail: { batchId: json.data.batch_id } }))
    } catch (err) {
      alert(`네트워크 오류: ${err.message}`)
      this.submitBtnTarget.disabled = false
    }
  }

  // helpers
  escape(s) {
    const div = document.createElement("div")
    div.appendChild(document.createTextNode(String(s)))
    return div.innerHTML
  }

  fileSizeLabel(bytes) {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`
  }
}
